import React, {useMemo, useState} from 'react';
import {Tooltip} from 'antd';
import brazil from '@svg-maps/brazil';
import {CHART_MUTED_TEXT, MAP_BORDER_COLOR, MAP_NO_DATA_FILL, SEQUENTIAL_BLUE} from './chartColors';

function hexToRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const RAMP_RGB = SEQUENTIAL_BLUE.map(hexToRgb);

// Interpola continuamente ao longo da rampa sequencial (steps 100→700) em vez de
// arredondar para o step mais próximo — mantém a magnitude legível para qualquer
// valor de receita, não só os 13 pontos documentados.
function sequentialColor(t) {
    const pos = Math.min(Math.max(t, 0), 1) * (RAMP_RGB.length - 1);
    const i = Math.floor(pos);
    const frac = pos - i;
    const [r1, g1, b1] = RAMP_RGB[i];
    const [r2, g2, b2] = RAMP_RGB[Math.min(i + 1, RAMP_RGB.length - 1)];
    const mix = (a, b) => Math.round(a + (b - a) * frac);
    return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
}

// Mapa coroplético do Brasil por UF. `data` é a lista de linhas da API (uma por
// estado com pelo menos 1 pedido no período); estados ausentes ficam com
// preenchimento neutro de "sem dado". `tooltipFields` segue o mesmo formato usado
// pelos outros gráficos de relatório (HorizontalBarChart etc.).
export default function BrazilMapChart({data, dataKey, nameKey, valueFormatter, tooltipFields, height = 420}) {
    const [hoveredId, setHoveredId] = useState(null);

    const byName = useMemo(() => {
        const map = new Map();
        (data || []).forEach((row) => map.set(row[nameKey], row));
        return map;
    }, [data, nameKey]);

    const [min, max] = useMemo(() => {
        const values = (data || []).map((row) => row[dataKey]).filter((v) => typeof v === 'number');
        if (!values.length) return [0, 0];
        return [Math.min(...values), Math.max(...values)];
    }, [data, dataKey]);

    const fields = tooltipFields || [{key: dataKey, label: 'Valor', formatter: valueFormatter}];

    return (
        <div>
            <svg viewBox={brazil.viewBox} width="100%" height={height} role="img" aria-label={brazil.label}>
                {brazil.locations.map((location) => {
                    const row = byName.get(location.name);
                    const value = row?.[dataKey];
                    const fill = typeof value === 'number'
                        ? sequentialColor(max === min ? 1 : (value - min) / (max - min))
                        : MAP_NO_DATA_FILL;
                    const isHovered = hoveredId === location.id;

                    const tooltipTitle = (
                        <div style={{fontSize: 12}}>
                            <div style={{fontWeight: 600, marginBottom: row ? 4 : 0}}>{location.name}</div>
                            {row ? (
                                fields.map((field) => (
                                    <div key={field.key} style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                        <span style={{color: '#c3c2b7'}}>{field.label}</span>
                                        <span>{field.formatter ? field.formatter(row[field.key]) : row[field.key]}</span>
                                    </div>
                                ))
                            ) : (
                                <span style={{color: '#c3c2b7'}}>Sem pedidos no período</span>
                            )}
                        </div>
                    );

                    return (
                        <Tooltip key={location.id} title={tooltipTitle}>
                            <path
                                d={location.path}
                                fill={fill}
                                stroke={MAP_BORDER_COLOR}
                                strokeWidth={isHovered ? 1.5 : 0.75}
                                style={{cursor: 'pointer', transition: 'stroke-width 0.1s'}}
                                onMouseEnter={() => setHoveredId(location.id)}
                                onMouseLeave={() => setHoveredId(null)}
                            />
                        </Tooltip>
                    );
                })}
            </svg>

            {max > min && (
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: CHART_MUTED_TEXT}}>
                    <span>{valueFormatter ? valueFormatter(min) : min}</span>
                    <div style={{
                        flex: 1,
                        maxWidth: 160,
                        height: 8,
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${SEQUENTIAL_BLUE[0]}, ${SEQUENTIAL_BLUE[SEQUENTIAL_BLUE.length - 1]})`,
                    }}/>
                    <span>{valueFormatter ? valueFormatter(max) : max}</span>
                </div>
            )}
        </div>
    );
}
